// printUtils.js
// Print helper for the Print Notes module.
//
//   printHtmlDocument(html) -> opens the browser print dialog with the form(s)
//                              rendered in an isolated, hidden iframe. Uses the
//                              form's own @media print CSS (vector-quality), and
//                              the native dialog can "Save as PDF".

const waitForRender = async (iframe) => {
  const idoc = iframe.contentWindow.document;
  // Give layout + webfonts a beat to settle so the captured/printed page is complete.
  if (idoc.fonts && idoc.fonts.ready) {
    try {
      await Promise.race([idoc.fonts.ready, new Promise((r) => setTimeout(r, 1500))]);
    } catch (e) {
      /* fonts.ready can reject in some browsers — fall through */
    }
  }
  await new Promise((r) => setTimeout(r, 150));
};

const mountHiddenIframe = (html) =>
  new Promise((resolve) => {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.border = "0";
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.onload = () => resolve(iframe);
    document.body.appendChild(iframe);
    const idoc = iframe.contentWindow.document;
    idoc.open();
    idoc.write(html);
    idoc.close();
    // Some browsers don't fire onload for document.write iframes — fall back.
    setTimeout(() => resolve(iframe), 400);
  });

/**
 * Print a complete HTML document (from buildDocument) via a hidden iframe.
 * Returns a promise that resolves once the print dialog has been invoked.
 */
export const printHtmlDocument = async (html) => {
  const iframe = await mountHiddenIframe(html);
  await waitForRender(iframe);
  const win = iframe.contentWindow;
  try {
    win.focus();
    win.print();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("print failed", e);
  }
  // Leave the iframe around briefly so the dialog has its document, then clean up.
  setTimeout(() => {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  }, 60000);
};

export default printHtmlDocument;
